// Integrating Multi factor authentication
import React, { useState, useEffect, useRef } from "react";
import { X, Upload, Search, Eye, EyeOff, Download, Edit } from "lucide-react"; // Added Edit icon
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Logo from "../Images/logos-criterion.png";
import ReactQuill from "react-quill"; // Import ReactQuill
import "react-quill/dist/quill.snow.css"; // Import Quill styles

const Blog = () => {
  const [showBlogPage, setShowBlogPage] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBlogDetails, setShowBlogDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false); // State for edit modal
  const [editingBlog, setEditingBlog] = useState(null); // State to hold blog being edited

  const [blogs, setBlogs] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    author: "",
    tags: "",
    image: "",
    metaTitle: "",
    metaDescription: "",
    focusKeyword: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const blogSectionRef = useRef(null);
  const blogItemsRef = useRef([]);
  const location = useLocation();
  const navigate = useNavigate();

  const userType = location.state?.userType || "user";
  const isAdmin = userType === "admin";

  // Fetch blogs on component mount
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch("https://criterionscreeners.com/api/blogs");
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setBlogs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching blogs:", error.message);
        setBlogs([]);
      }
    };
    fetchBlogs();
  }, []);

  // Handle admin authentication flow
  useEffect(() => {
    if (isAdmin) {
      setShowEmailModal(true); // Show email modal for admin first
    } else {
      setShowBlogPage(true); // Show blog page directly for non-admins
    }
  }, [isAdmin]);

  // Intersection Observer for animations
  useEffect(() => {
    if (showBlogPage) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("opacity-100", "translate-y-0");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      if (blogSectionRef.current) {
        observer.observe(blogSectionRef.current);
      }

      blogItemsRef.current.forEach((item) => {
        if (item) observer.observe(item);
      });

      return () => {
        if (blogSectionRef.current) {
          observer.unobserve(blogSectionRef.current);
        }
        blogItemsRef.current.forEach((item) => {
          if (item) observer.unobserve(item);
        });
      };
    }
  }, [showBlogPage, blogs]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle content change from ReactQuill
  const handleContentChange = (value) => {
    setFormData({ ...formData, content: value });
  };

  // Reset form data
  const resetFormData = () => {
    setFormData({
      title: "",
      excerpt: "",
      content: "",
      author: "",
      tags: "",
      image: "",
      metaTitle: "",
      metaDescription: "",
      focusKeyword: "",
    });
  };

  // Handle Create Blog Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newBlog = {
      // ... (spread formData)
      ...formData,
      tags: formData.tags.split(",").map((tag) => tag.trim()),
      image:
        formData.image ||
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      // date is handled by backend now
    };

    try {
      const response = await fetch("https://criterionscreeners.com/api/blogs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newBlog),
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const savedBlog = await response.json();
      setBlogs([savedBlog, ...blogs]); // Add to the beginning
      resetFormData();
      setShowUploadForm(false);
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (error) {
      console.error("Error posting blog:", error.message);
      alert(`Failed to publish blog: ${error.message}`);
    }
  };

  // Handle Edit Blog Submit
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingBlog) return;

    const updatedBlogData = {
      ...formData, // Contains the latest data from the form, including HTML content
      tags:
        typeof formData.tags === "string"
          ? formData.tags.split(",").map((tag) => tag.trim())
          : formData.tags, // Ensure tags are array
    };

    try {
      const response = await fetch(
        `https://criterionscreeners.com/api/blogs/${editingBlog._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedBlogData),
        }
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const updatedBlog = await response.json();

      // Update the blog in the local state
      setBlogs(
        blogs.map((blog) => (blog._id === updatedBlog._id ? updatedBlog : blog))
      );

      setShowEditForm(false);
      setEditingBlog(null);
      resetFormData();
      // Optional: Show success message
      setShowSuccessPopup(true); // Reuse success popup
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (error) {
      console.error("Error updating blog:", error.message);
      alert(`Failed to update blog: ${error.message}`);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailPattern = /^[^\s@]+@criterionscreeners\.com$/;
    if (!emailPattern.test(email)) {
      setEmailError("The above entered Email ID is Denied!!!");
      return;
    }
    try {
      const response = await fetch("https://criterionscreeners.com/api/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        if (data.message === "Email verification sent") {
          setEmailError(
            `Verification email sent at ${data.createdAt}. Please wait for admin approval. Request expires at ${data.expiresAt}.`
          );
        } else if (data.message === "Email already approved") {
          setEmailError(
            `Email already approved. Access valid until ${data.accessExpiresAt}.`
          );
          setShowEmailModal(false);
          setShowPasswordModal(true); // Move to password modal
        }
      } else {
        setEmailError(data.message || "Error verifying email");
      }
    } catch (error) {
      setEmailError("Server error. Please try again.");
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    // const correctPassword = 'admin123';
    // Indirectly assigned password variable (Base64 encoded 'admin123')
    // const correctPassword = atob('YWRtaW4xMjM='); // Decodes to 'admin123' at runtime
    const correctPassword = atob("dmVyaWZ5IzUyNzIwMTk="); // Decodes to 'admin123' at runtime

    if (password === correctPassword) {
      setShowPasswordModal(false);
      setShowBlogPage(true);
    } else {
      setPasswordError("Incorrect password");
      setTimeout(() => navigate(-1), 1000);
    }
  };

  const handleDeleteBlog = async (id) => {
    try {
      const response = await fetch(`https://criterionscreeners.com/api/blogs/${id}`, {
        method: "DELETE",
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      setBlogs(blogs.filter((blog) => blog._id !== id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting blog:", error.message);
    }
  };

  const filteredBlogs = (Array.isArray(blogs) ? blogs : []).filter((blog) => {
    if (!searchTerm) return true;
    return (
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  });

  const handleReadMore = (blog) => {
    setShowBlogDetails(blog);
  };

  // Open Edit Modal
  const handleEditClick = (blog) => {
    setEditingBlog(blog);
    // Pre-fill form data, ensuring tags are a string for the input field
    setFormData({
      ...blog,
      tags: Array.isArray(blog.tags) ? blog.tags.join(", ") : blog.tags || "", // Join tags array into string
    });
    setShowEditForm(true);
  };

  // Close Edit Modal
  const closeEditModal = () => {
    setShowEditForm(false);
    setEditingBlog(null);
    resetFormData();
  };

  const handleDoubleClickBlog = (blog) => {
    if (isAdmin) {
      setShowDeleteConfirm(blog);
    }
  };

  const handleDownloadUserData = async () => {
    try {
      const response = await fetch(
        "https://criterionscreeners.com/api/export-user-details",
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "User-Data-Report.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading user data:", error.message);
      alert("Failed to download user data. Please try again.");
    }
  };

  // Quill Modules configuration (optional, customize as needed)
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"], // Add image upload/link if needed
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  return (
    <div className="font-sans">
      {/* SEO Meta Tags and Structured Data */}
      <Helmet>
        <title>
          {showBlogDetails
            ? showBlogDetails.metaTitle || showBlogDetails.title
            : "Criterion Screeners Blog"}
        </title>
        <meta
          name="description"
          content={
            showBlogDetails
              ? showBlogDetails.metaDescription || showBlogDetails.excerpt
              : "Discover expert analysis, tips, and strategies to enhance your investment decisions with Criterion Screeners."
          }
        />
        <meta
          name="keywords"
          content={
            showBlogDetails
              ? showBlogDetails.focusKeyword || showBlogDetails.tags.join(", ")
              : "investment, stocks, market analysis"
          }
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: showBlogDetails?.title || "Criterion Screeners Blog",
            author: {
              "@type": "Person",
              name: showBlogDetails?.author || "Criterion Screeners",
            },
            datePublished: showBlogDetails?.date || new Date().toISOString(),
            image:
              showBlogDetails?.image ||
              "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
            description:
              showBlogDetails?.excerpt ||
              "Discover expert analysis, tips, and strategies to enhance your investment decisions.",
          })}
        </script>
      </Helmet>

      {/* Email Verification Modal */}
      {showEmailModal && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in-up">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Enter Your Email
            </h3>
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your email"
                  required
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in-up">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Enter Admin Password
            </h3>
            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-4 relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {passwordError && (
                  <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blog Page Overlay */}
      {showBlogPage && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto transition-transform duration-500 ease-in-out">
          <header className="sticky top-0 bg-white shadow-md z-10">
            <div className="container mx-auto px-4 py-4 flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-0">
              {/* Logo Section */}
              <div
                className="flex items-center justify-center md:justify-start space-x-4 cursor-pointer" // Added cursor-pointer
                onClick={() => navigate("/")} // Added onClick handler to navigate to home
              >
                <img
                  src={Logo}
                  alt="Criterion Screeners Logo" // Updated alt text
                  className="h-16 md:h-20 transition-all duration-300"
                />
              </div>

              {/* Search and Buttons Section */}
              <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
                {/* Search Input */}
                <div className="relative w-full md:w-auto">
                  <input
                    type="text"
                    placeholder="Search blogs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-white pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black w-full"
                  />
                  <Search
                    className="absolute left-3 top-2.5 text-gray-400"
                    size={18}
                  />
                </div>

                {/* Admin Buttons (visible only to admins) */}
                {isAdmin && (
                  <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
                    <button
                      onClick={() => setShowUploadForm(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-105 w-full md:w-auto"
                    >
                      <Upload size={18} />
                      <span>New Post</span>
                    </button>
                    <button
                      onClick={handleDownloadUserData}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-full flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-105 w-full md:w-auto"
                    >
                      <Download size={18} />
                      <span>Download User Data</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="container mx-auto px-4 py-8">
            <section
              ref={blogSectionRef}
              className="mb-16 opacity-0 translate-y-10 transition-all duration-1000 ease-out"
            >
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl overflow-hidden shadow-xl">
                <div className="md:flex">
                  <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      Insights for Secure Background Verification
                    </h2>
                    <p className="text-blue-100 mb-6">
                      Discover expert analysis, best practices, and compliance
                      updates to enhance your background screening processes
                      with Criterion Screeners.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                        #BackgroundChecks
                      </span>
                      <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                        #EmployeeScreening
                      </span>
                      <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                        #Compliance
                      </span>
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <img
                      src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                      alt="Investment Analysis"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-16 flex flex-col lg:flex-row gap-8">
              {/* Main Blog Posts Area */}
              <div className="lg:w-2/3">
                <h3 className="text-2xl font-bold text-gray-800 mb-8">
                  Welcome to Our Blog
                </h3>
                <div className="space-y-8">
                  {filteredBlogs.length === 0 && searchTerm ? (
                    <div className="col-span-full text-center py-12">
                      <p className="text-gray-500 text-lg">
                        No blog posts found matching your search.
                      </p>
                    </div>
                  ) : (
                    filteredBlogs.map((blog, index) => (
                      <article
                        key={blog._id}
                        ref={(el) => (blogItemsRef.current[index] = el)}
                        className={`bg-white rounded-xl shadow-md overflow-hidden opacity-0 translate-y-10 transition-all duration-700 ease-out ${
                          index === 0 ? "md:flex" : ""
                        }`}
                        style={{ transitionDelay: `${index * 150}ms` }}
                        onDoubleClick={() =>
                          isAdmin && handleDoubleClickBlog(blog)
                        } // Keep delete on double click for admin
                      >
                        {index === 0 ? (
                          <>
                            <div className="md:w-1/2 h-64 md:h-auto overflow-hidden">
                              <img
                                src={blog.image}
                                alt={blog.title}
                                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                              />
                            </div>
                            <div className="md:w-1/2 p-6">
                              <h4 className="text-2xl font-bold text-gray-800 mb-2 line-clamp-2">
                                {blog.title}
                              </h4>
                              <p className="text-gray-600 mb-4 line-clamp-3">
                                {blog.excerpt}
                              </p>
                              <div className="flex flex-wrap gap-2 mb-4">
                                {blog.tags.map((tag, i) => (
                                  <span
                                    key={i}
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">
                                  By {blog.author}
                                </span>
                                <div>
                                  {" "}
                                  {/* Container for buttons */}
                                  {isAdmin && ( // Show Edit button only for admin
                                    <button
                                      onClick={() => handleEditClick(blog)}
                                      className="text-green-600 hover:text-green-800 text-sm font-semibold transition-colors mr-3" // Added margin
                                      aria-label={`Edit ${blog.title}`}
                                    >
                                      <Edit
                                        size={16}
                                        className="inline-block mr-1"
                                      />{" "}
                                      Edit
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleReadMore(blog)}
                                    className="text-blue-600 hover:text-blue-800 text-sm font-semibold transition-colors"
                                  >
                                    Read More
                                  </button>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="relative h-48 overflow-hidden">
                              <img
                                src={blog.image}
                                alt={blog.title}
                                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                              />
                              <div className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 m-2 rounded-full text-xs font-semibold">
                                {blog.date}
                              </div>
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                                {blog.title}
                              </h4>
                              <p className="text-gray-600 mb-4 line-clamp-3">
                                {blog.excerpt}
                              </p>
                              <div className="flex flex-wrap gap-2 mb-4">
                                {blog.tags.map((tag, i) => (
                                  <span
                                    key={i}
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">
                                  By {blog.author}
                                </span>
                                <div>
                                  {" "}
                                  {/* Container for buttons */}
                                  {isAdmin && ( // Show Edit button only for admin
                                    <button
                                      onClick={() => handleEditClick(blog)}
                                      className="text-green-600 hover:text-green-800 text-sm font-semibold transition-colors mr-3" // Added margin
                                      aria-label={`Edit ${blog.title}`}
                                    >
                                      <Edit
                                        size={16}
                                        className="inline-block mr-1"
                                      />{" "}
                                      Edit
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleReadMore(blog)}
                                    className="text-blue-600 hover:text-blue-800 text-sm font-semibold transition-colors"
                                  >
                                    Read More
                                  </button>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </article>
                    ))
                  )}
                </div>
              </div>

              {/* Sidebar Area */}
              <aside className="lg:w-1/3 space-y-8">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">
                    Recent Posts
                  </h4>
                  <ul className="space-y-4">
                    {filteredBlogs.slice(0, 3).map((blog, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <img
                          src={blog.image}
                          alt={blog.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <h5 className="text-sm font-semibold text-gray-800 line-clamp-1">
                            {blog.title}
                          </h5>
                          <p className="text-xs text-gray-500">{blog.date}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">
                    Popular Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {filteredBlogs
                      .flatMap((blog) => blog.tags)
                      .filter((tag, index, self) => self.indexOf(tag) === index)
                      .slice(0, 5)
                      .map((tag, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                  </div>
                </div>
              </aside>
            </section>
          </main>

          <footer className="bg-gray-800 text-white py-8">
            <div className="container mx-auto px-4">
              <div className="text-center">
                <p>
                  © {new Date().getFullYear()} Criterion Screeners. All rights
                  reserved.
                </p>
              </div>
            </div>
          </footer>
        </div>
      )}

      {/* Blog Details Modal */}
      {showBlogDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  {showBlogDetails.title}
                </h3>
                <button
                  onClick={() => setShowBlogDetails(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              <img
                src={showBlogDetails.image}
                alt={showBlogDetails.title}
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              <div
                className="prose max-w-none text-gray-700 mb-4" // Added prose class for basic styling
                dangerouslySetInnerHTML={{ __html: showBlogDetails.content }}
              />
              <div className="flex flex-wrap gap-2 mb-4">
                {showBlogDetails.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="text-sm text-gray-500">
                By {showBlogDetails.author} | {showBlogDetails.date}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{showDeleteConfirm.title}"?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBlog(showDeleteConfirm._id)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed bottom-10 right-10 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[70] animate-popper">
          <span className="font-semibold">Blog Published Successfully!</span>
        </div>
      )}

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  Create New Blog Post
                </h3>
                <button
                  onClick={() => {
                    setShowUploadForm(false);
                    resetFormData();
                  }} // Reset form on close
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Title*
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                      placeholder="Enter blog title"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="excerpt"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Excerpt*
                    </label>
                    <input
                      type="text"
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      required
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief summary of your blog post"
                    />
                  </div>

                  {/* Content Field using ReactQuill */}
                  <div>
                    <label
                      htmlFor="content"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Content*
                    </label>
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={handleContentChange}
                      modules={quillModules}
                      formats={quillFormats}
                      className="bg-white text-black" // Ensure proper styling context
                      placeholder="Write your blog content here..."
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="author"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Author*
                    </label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      required
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="tags"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Tags* (comma separated)
                    </label>
                    <input
                      type="text"
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      required
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Investing, Stocks, Analysis"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="image"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Image URL (optional)
                    </label>
                    <input
                      type="url"
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleInputChange}
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">
                      SEO Settings
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label
                          htmlFor="metaTitle"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Meta Title
                        </label>
                        <input
                          type="text"
                          id="metaTitle"
                          name="metaTitle"
                          value={formData.metaTitle}
                          onChange={handleInputChange}
                          className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="SEO optimized title (defaults to post title)"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="metaDescription"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Meta Description
                        </label>
                        <textarea
                          id="metaDescription"
                          name="metaDescription"
                          value={formData.metaDescription}
                          onChange={handleInputChange}
                          rows={2}
                          className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Brief description for search engines (150-160 characters recommended)"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="focusKeyword"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Focus Keyword
                        </label>
                        <input
                          type="text"
                          id="focusKeyword"
                          name="focusKeyword"
                          value={formData.focusKeyword}
                          onChange={handleInputChange}
                          className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Main keyword for this post"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadForm(false);
                      resetFormData();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Publish Post
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && editingBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  Edit Blog Post
                </h3>
                <button
                  onClick={closeEditModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdate}>
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label
                      htmlFor="edit-title"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Title*
                    </label>
                    <input
                      type="text"
                      id="edit-title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                    />
                  </div>
                  {/* Excerpt */}
                  <div>
                    <label
                      htmlFor="edit-excerpt"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Excerpt*
                    </label>
                    <input
                      type="text"
                      id="edit-excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      required
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {/* Content */}
                  <div>
                    <label
                      htmlFor="edit-content"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Content*
                    </label>
                    <ReactQuill
                      theme="snow"
                      value={formData.content} // Bind to formData.content
                      onChange={handleContentChange} // Use the same handler
                      modules={quillModules}
                      formats={quillFormats}
                      className="bg-white text-black"
                    />
                  </div>
                  {/* Author */}
                  <div>
                    <label
                      htmlFor="edit-author"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Author*
                    </label>
                    <input
                      type="text"
                      id="edit-author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      required
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {/* Tags */}
                  <div>
                    <label
                      htmlFor="edit-tags"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Tags* (comma separated)
                    </label>
                    <input
                      type="text"
                      id="edit-tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      required
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {/* Image URL */}
                  <div>
                    <label
                      htmlFor="edit-image"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Image URL
                    </label>
                    <input
                      type="url"
                      id="edit-image"
                      name="image"
                      value={formData.image}
                      onChange={handleInputChange}
                      className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {/* SEO Settings */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">
                      SEO Settings
                    </h4>
                    <div className="space-y-3">
                      {/* Meta Title */}
                      <div>
                        <label
                          htmlFor="edit-metaTitle"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Meta Title
                        </label>
                        <input
                          type="text"
                          id="edit-metaTitle"
                          name="metaTitle"
                          value={formData.metaTitle}
                          onChange={handleInputChange}
                          className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {/* Meta Description */}
                      <div>
                        <label
                          htmlFor="edit-metaDescription"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Meta Description
                        </label>
                        <textarea
                          id="edit-metaDescription"
                          name="metaDescription"
                          value={formData.metaDescription}
                          onChange={handleInputChange}
                          rows={2}
                          className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {/* Focus Keyword */}
                      <div>
                        <label
                          htmlFor="edit-focusKeyword"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Focus Keyword
                        </label>
                        <input
                          type="text"
                          id="edit-focusKeyword"
                          name="focusKeyword"
                          value={formData.focusKeyword}
                          onChange={handleInputChange}
                          className="text-black bg-white w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" // Changed color for Update
                  >
                    Update Post
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Popper Animation CSS */}
      <style jsx>{`
        @keyframes popper {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-popper {
          animation: popper 0.5s ease-out forwards;
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Blog;
